import { useState, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { AppRouter } from './routes';
import { ToastProvider } from './components/ToastProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SplashScreen } from './components/SplashScreen';
import { OfflineIndicator } from './components/OfflineIndicator';
import { MaintenancePage } from './components/MaintenancePage';
import { initializeTheme } from './store/useThemeStore';
import { useSplashStore } from './store/useSplashStore';

/**
 * Root App Component
 * Sets up routing, theme, and global providers
 * Wrapped with ErrorBoundary to catch any uncaught errors
 * Features a premium splash screen on initial load
 * Includes offline support and resilience features
 */
function App() {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const [serverDown, setServerDown] = useState(false);

  // Initialize theme on mount
  useEffect(() => {
    initializeTheme();
    
    // Check if server is reachable (only when online)
    const checkServerHealth = async () => {
      if (!navigator.onLine) {
        setIsInitialLoading(false);
        return;
      }
      
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch(`${apiUrl}/health`, {
          signal: controller.signal,
          mode: 'cors',
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          // Server responded but with error - might be maintenance
          // Don't show maintenance page for non-critical errors
        }
        
        setServerDown(false);
      } catch {
        // Server unreachable - use cached data instead
        // Only show maintenance if there's no cached data available
        const hasCache = localStorage.getItem('YIM_QUERY_CACHE');
        if (!hasCache) {
          setServerDown(true);
        }
      } finally {
        // Add small delay for smooth transition
        await new Promise(resolve => setTimeout(resolve, 100));
        setIsInitialLoading(false);
      }
    };

    checkServerHealth();
  }, []);

  const handleSplashComplete = () => {
    setShowSplash(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        useSplashStore.getState().markComplete();
      });
    });
  };

  // Handle maintenance mode retry
  const handleMaintenanceRetry = () => {
    setServerDown(false);
    setIsInitialLoading(true);
    window.location.reload();
  };

  // Custom error fallback that shows maintenance page
  const renderErrorFallback = (error: Error) => (
    <MaintenancePage 
      type="error" 
      error={error} 
      onRetry={() => window.location.reload()} 
    />
  );

  return (
    <HelmetProvider>
      <ErrorBoundary fallback={renderErrorFallback}>
        {/* Premium Splash Screen - shown on initial load */}
        {showSplash && (
          <SplashScreen 
            minDuration={1800}
            isLoading={isInitialLoading}
            onComplete={handleSplashComplete}
          />
        )}
        
        {/* Maintenance Page - shown when server is completely unreachable and no cache */}
        {serverDown && !showSplash && (
          <MaintenancePage 
            type="maintenance" 
            onRetry={handleMaintenanceRetry} 
          />
        )}
        
        {/* Main App Content */}
        {!serverDown && (
          <ToastProvider>
            {/* Offline Indicator - shows when connection is lost */}
            <OfflineIndicator position="bottom" showCacheIndicator />
            
            <AppRouter />
          </ToastProvider>
        )}
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
